/**
 * Scene3DStage — layer-definition helpers.
 *
 * Pure, closure-free helpers/constants split out of `Scene3DStage.tsx`
 * (2026-07-12) to keep that file under CLAUDE.md's file-size guidance.
 * NO behaviour change: the symbols are re-imported by `Scene3DStage.tsx`
 * verbatim, so every call site resolves exactly as before.
 */

import type { LayerDefinition } from "@dxf-ai-editor/drawing-model";
import { addLayerChain } from "./layerChainVisibility";
import { addSatelliteLayers, type SatellitesByParent } from "./scene/satelliteLayerProjection";
import type { SpatialIndex } from "./types";

/**
 * Stable-identity empty layer-definition array used as the fallback for the
 * `rawLayers` store selector — a fresh `[]` per render would defeat Zustand's
 * reference equality and re-run downstream memos every tick.
 */
export const EMPTY_LAYER_DEFS: LayerDefinition[] = [];

/**
 * Build the set of layer names that have at least one entity in `visibleIds`.
 * Used to hide batched line-segment buckets whose layer has no VP-visible
 * entity (P1 #3 layer-level bucket culling).
 *
 * `satellitesByParent` (Codex PR #784 P1) — the render-only satellites of the
 * visible parents (INSERT wrapper children on the cache-direct path, MLEADER
 * block content) are absent from the spatial index but DRAW into buckets
 * keyed by their OWN layer; their layers are projected here from the same
 * map the render feed expands them from, so a satellite-only layer's bucket
 * is not culled as "absent" (`scene/satelliteLayerProjection.ts`).
 *
 * Every entity's block-reference layer CHAIN is projected as well
 * (`addLayerChain`, round 3 P1): a `(B, parents=[A])` bucket's VP gate
 * requires A to be present too, and on the legacy branch the INSERT on A is
 * not itself an index entry — only its children (carrying the chain) are.
 */
export function computeVisibleLayers(
  visibleIds: Set<string>,
  spatial: SpatialIndex,
  satellitesByParent?: SatellitesByParent,
): Set<string> {
  const layers = new Set<string>();
  for (const id of visibleIds) {
    const entity = spatial.resolve(id);
    if (!entity) continue;
    addLayerChain(entity, layers);
    addSatelliteLayers(entity, satellitesByParent, layers);
  }
  return layers;
}
